/**
 * Loads content into the draw page's editor.
 *
 * The editor instance is absent while an editor is still booting, hence the guard. Callers choose
 * whether the load is additive or replacing so inserting a named structure cannot accidentally
 * inherit the example-reaction replacement behaviour.
 *
 * @param {{ current?: { instance?: object } }} editorInstanceRef ref handed to `<StructureEditor editorInstanceRef>`
 * @param {(instance: object) => unknown} load invokes the load method that matches the payload format
 * @param {boolean} replace whether to clear the editor before loading
 * @returns {Promise<boolean>} resolves true when the payload reached an editor instance
 */
async function loadEditorContent(editorInstanceRef, load, replace = false) {
  const instance = editorInstanceRef?.current?.instance;
  if (!instance) return false;

  try {
    // ChemDraw clears asynchronously. Await explicit replacement clears so they cannot resolve
    // after the new content lands and wipe it. Structure-from-name uses each editor's load semantics.
    if (replace) await instance?.clear?.();
    await load(instance);
    return true;
  } catch (error) {
    Logger.warn('Conditions insert structure failed with error:', error);
    return false;
  }
}

/** Loads editor-native structure data using the active editor's native semantics. */
export function insertStructureIntoEditor(editorInstanceRef, structure) {
  return loadEditorContent(editorInstanceRef, (instance) => instance?.loadStructure?.(structure));
}

/** Loads a structure or reaction SMILES, which the editors parse through their own SMILES readers. */
export function insertSmilesIntoEditor(editorInstanceRef, smiles) {
  return loadEditorContent(editorInstanceRef, (instance) => instance?.loadSmiles?.(smiles), true);
}
